import AccessibleModal from "../AccessibleModal";
import React, { useEffect, useMemo, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

const EXAMPLE_GROSS_CENTS = 3000;

function moneyFromCents(value) {
  const cents = Number(value || 0);
  return new Intl.NumberFormat("es-PA", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

function percentFromBps(value) {
  return `${(Number(value || 0) / 100).toFixed(2)}%`;
}

function decimalPercentToBps(value) {
  const raw = String(value ?? "").trim();
  if (!raw) throw new Error("Ingresa el porcentaje.");
  const n = Number(raw.replace(",", "."));
  if (!Number.isFinite(n) || n < 0 || n > 100) {
    throw new Error("El porcentaje debe estar entre 0 y 100.");
  }
  return Math.round(n * 100);
}

function dollarsToCents(value) {
  const raw = String(value ?? "").trim();
  if (!raw) throw new Error("Ingresa el cargo fijo.");
  const n = Number(raw.replace(",", "."));
  if (!Number.isFinite(n) || n < 0) {
    throw new Error("El cargo fijo debe ser un monto válido.");
  }
  return Math.round(n * 100);
}

function centsFromPercentAndFixed(grossCents, percentText, fixedText) {
  const bps = decimalPercentToBps(percentText);
  const fixed = dollarsToCents(fixedText);
  return Math.round((grossCents * bps) / 10000) + fixed;
}

function localDateTimeDefault(minutesAhead = 1) {
  const d = new Date(Date.now() + minutesAhead * 60 * 1000);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60 * 1000);
  return local.toISOString().slice(0, 16);
}

function toIsoFromLocal(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) throw new Error("Selecciona una fecha efectiva válida.");
  return d.toISOString();
}

function callableErrorMessage(error) {
  return String(
    error?.message ||
      error?.details?.message ||
      error?.details ||
      "No se pudo completar la operación.",
  ).replace(/^Firebase:\s*/i, "");
}

function businessName(business) {
  return business?.businessName || business?.name || business?.displayName || business?.title || business?.id || "Negocio";
}

function economicsPreview({ processorPercent, processorFixed, merchantPercent, merchantFixed }) {
  try {
    const processorCost = centsFromPercentAndFixed(EXAMPLE_GROSS_CENTS, processorPercent, processorFixed);
    const merchantFee = Math.min(
      EXAMPLE_GROSS_CENTS,
      centsFromPercentAndFixed(EXAMPLE_GROSS_CENTS, merchantPercent, merchantFixed),
    );
    return {
      valid: true,
      processorCost,
      merchantFee,
      merchantPayable: Math.max(0, EXAMPLE_GROSS_CENTS - merchantFee),
      loopMargin: merchantFee - processorCost,
    };
  } catch (_) {
    return { valid: false };
  }
}

function scheduleLabel(schedule) {
  if (!schedule) return "Sin tarifa activa";
  return `${percentFromBps(schedule.processor?.percentBps)} + ${moneyFromCents(schedule.processor?.fixedCents)} Wompi · ${percentFromBps(schedule.merchant?.percentBps)} + ${moneyFromCents(schedule.merchant?.fixedCents)} Loop`;
}

export default function OwnerFinanceSettingsPanel({ businesses = [] }) {
  const [editor, setEditor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [savingOverride, setSavingOverride] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [data, setData] = useState(null);
  const [selectedBusinessId, setSelectedBusinessId] = useState("");

  const [globalForm, setGlobalForm] = useState({
    processorPercent: "",
    processorFixed: "",
    merchantPercent: "",
    merchantFixed: "",
    effectiveFrom: localDateTimeDefault(),
  });

  const [overrideForm, setOverrideForm] = useState({
    merchantPercent: "",
    merchantFixed: "",
    effectiveFrom: localDateTimeDefault(),
    note: "",
  });

  const functions = useMemo(() => getFunctions(), []);

  const load = async (businessId = selectedBusinessId) => {
    setLoading(true);
    setError("");
    try {
      const fn = httpsCallable(functions, "ownerGetPlatformFinanceConfig");
      const response = await fn({ businessId: businessId || null });
      const next = response?.data || {};
      setData(next);

      const active = next.activeGlobalSchedule;
      if (active) {
        setGlobalForm((current) => ({
          ...current,
          processorPercent: (Number(active.processor?.percentBps || 0) / 100).toFixed(2),
          processorFixed: (Number(active.processor?.fixedCents || 0) / 100).toFixed(2),
          merchantPercent: (Number(active.merchant?.percentBps || 0) / 100).toFixed(2),
          merchantFixed: (Number(active.merchant?.fixedCents || 0) / 100).toFixed(2),
        }));
      }

      const activeOverride = next.activeBusinessOverride;
      if (activeOverride) {
        setOverrideForm((current) => ({
          ...current,
          merchantPercent: (Number(activeOverride.merchant?.percentBps || 0) / 100).toFixed(2),
          merchantFixed: (Number(activeOverride.merchant?.fixedCents || 0) / 100).toFixed(2),
          note: activeOverride.note || "",
        }));
      } else {
        setOverrideForm((current) => ({
          ...current,
          merchantPercent: "",
          merchantFixed: "",
          note: "",
        }));
      }
    } catch (err) {
      setError(callableErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const globalPreview = useMemo(
    () => economicsPreview(globalForm),
    [globalForm],
  );

  const effectiveMerchantPercent = overrideForm.merchantPercent || globalForm.merchantPercent;
  const effectiveMerchantFixed = overrideForm.merchantFixed || globalForm.merchantFixed;
  const overridePreview = useMemo(
    () =>
      economicsPreview({
        ...globalForm,
        merchantPercent: effectiveMerchantPercent,
        merchantFixed: effectiveMerchantFixed,
      }),
    [globalForm, effectiveMerchantPercent, effectiveMerchantFixed],
  );

  const saveGlobal = async () => {
    setSavingGlobal(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        effectiveFrom: toIsoFromLocal(globalForm.effectiveFrom),
        processorPercentBps: decimalPercentToBps(globalForm.processorPercent),
        processorFixedCents: dollarsToCents(globalForm.processorFixed),
        merchantPercentBps: decimalPercentToBps(globalForm.merchantPercent),
        merchantFixedCents: dollarsToCents(globalForm.merchantFixed),
      };
      const fn = httpsCallable(functions, "ownerCreatePlatformFeeSchedule");
      const response = await fn(payload);
      const created = response?.data?.created === true;
      setSuccess(created ? "Nueva tarifa global creada." : "La tarifa ya existía; no se duplicó.");
      setGlobalForm((current) => ({ ...current, effectiveFrom: localDateTimeDefault() }));
      await load(selectedBusinessId);
    } catch (err) {
      setError(callableErrorMessage(err));
    } finally {
      setSavingGlobal(false);
    }
  };

  const saveOverride = async () => {
    if (!selectedBusinessId) {
      setError("Selecciona un negocio antes de crear un override.");
      return;
    }
    setSavingOverride(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        businessId: selectedBusinessId,
        effectiveFrom: toIsoFromLocal(overrideForm.effectiveFrom),
        merchantPercentBps: decimalPercentToBps(overrideForm.merchantPercent),
        merchantFixedCents: dollarsToCents(overrideForm.merchantFixed),
        note: overrideForm.note || null,
      };
      const fn = httpsCallable(functions, "ownerCreateBusinessFeeOverride");
      const response = await fn(payload);
      const created = response?.data?.created === true;
      setSuccess(created ? "Override del negocio creado." : "Ese override ya existía; no se duplicó.");
      setOverrideForm((current) => ({ ...current, effectiveFrom: localDateTimeDefault() }));
      await load(selectedBusinessId);
    } catch (err) {
      setError(callableErrorMessage(err));
    } finally {
      setSavingOverride(false);
    }
  };

  const onBusinessChange = async (event) => {
    const nextId = event.target.value;
    setSelectedBusinessId(nextId);
    setSuccess("");
    await load(nextId);
  };

  const active = data?.activeGlobalSchedule || null;
  const activeOverride = data?.activeBusinessOverride || null;
  const serverPreview = data?.preview30 || null;
  const warnings = data?.warnings || {};

  return (
    <section className="owner-finance-settings">
      <style>{styles}</style>

      <div className="ofs-head">
        <div>
          <span className="ofs-kicker">Loop Finance</span>
          <h2>Tarifas de procesamiento</h2>
          <p>Configura el costo contractual de Wompi y lo que Loop cobra al negocio. Cada nueva tarifa empieza a aplicar desde su fecha efectiva; el histórico no se recalcula.</p>
        </div>
        <button type="button" className="ofs-secondary" onClick={() => load(selectedBusinessId)} disabled={loading}>
          {loading ? "Actualizando…" : "Actualizar"}
        </button>
      </div>

      {error ? <div className="ofs-alert ofs-error">{error}</div> : null}
      {success ? <div className="ofs-alert ofs-success">{success}</div> : null}
      {warnings.overlappingGlobalSchedules > 0 ? (
        <div className="ofs-alert ofs-warning">Hay {warnings.overlappingGlobalSchedules + 1} tarifas globales efectivas en esta fecha. Loop usará la de fecha efectiva más reciente.</div>
      ) : null}

      <div className="ofs-status-grid">
        <article>
          <span>Tarifa global efectiva</span>
          <strong>{active ? scheduleLabel(active) : "Todavía no configurada"}</strong>
          <small>{active?.effectiveFrom ? `Desde ${new Date(active.effectiveFrom).toLocaleString("es-PA")}` : "Crea la primera tarifa para activar la matemática financiera."}</small>
        </article>
        <article>
          <span>Ejemplo actual · $30</span>
          <strong>{serverPreview ? `${moneyFromCents(serverPreview.merchantPayableCents)} al negocio` : "—"}</strong>
          <small>{serverPreview ? `${moneyFromCents(serverPreview.loopProcessingMarginCents)} margen Loop · ${moneyFromCents(serverPreview.processorCostCents)} costo Wompi` : "Sin tarifa activa"}</small>
        </article>
      </div>

      <div className="ofs-two-columns">
        <article className="ofs-card"><h3>Tarifa estándar</h3><p>Costo de Wompi y comisión de Loop para nuevos cobros.</p><button className="ofs-primary" onClick={() => setEditor("global")}>Configurar tarifa</button></article>
        <article className="ofs-card"><h3>Tarifa por negocio</h3><p>Consulta y ajusta condiciones comerciales por empresa.</p><button className="ofs-primary" onClick={() => setEditor("business")}>Elegir negocio y ajustar</button></article>
      </div>
      {editor && <AccessibleModal open layer="stacked" width={640} labelledBy="finance-editor-title" onClose={savingGlobal || savingOverride ? undefined : () => setEditor(null)}>
        <div className="owner-finance-settings"><h2 id="finance-editor-title">{editor === "global" ? "Tarifa estándar" : "Tarifa del negocio"}</h2>
        {error && <p role="alert">{error}</p>}{success && <p role="status">{success}</p>}
        {editor === "global" ? (<article className="ofs-card">
          <div className="ofs-card-head">
            <div>
              <span className="ofs-card-kicker">Global</span>
              <h3>Nueva tarifa estándar</h3>
            </div>
            <span className="ofs-pill">USD · Wompi</span>
          </div>

          <div className="ofs-rate-pair">
            <div>
              <h4>Costo contractual Wompi</h4>
              <div className="ofs-fields">
                <label><span>Porcentaje</span><div><input inputMode="decimal" placeholder="3.00" value={globalForm.processorPercent} onChange={(e) => setGlobalForm((x) => ({ ...x, processorPercent: e.target.value }))} /><b>%</b></div></label>
                <label><span>Fijo / transacción</span><div><b>$</b><input inputMode="decimal" placeholder="0.25" value={globalForm.processorFixed} onChange={(e) => setGlobalForm((x) => ({ ...x, processorFixed: e.target.value }))} /></div></label>
              </div>
            </div>
            <div>
              <h4>Tarifa Loop al negocio</h4>
              <div className="ofs-fields">
                <label><span>Porcentaje</span><div><input inputMode="decimal" placeholder="4.00" value={globalForm.merchantPercent} onChange={(e) => setGlobalForm((x) => ({ ...x, merchantPercent: e.target.value }))} /><b>%</b></div></label>
                <label><span>Fijo / transacción</span><div><b>$</b><input inputMode="decimal" placeholder="0.50" value={globalForm.merchantFixed} onChange={(e) => setGlobalForm((x) => ({ ...x, merchantFixed: e.target.value }))} /></div></label>
              </div>
            </div>
          </div>

          <label className="ofs-date-field"><span>Aplicar desde</span><input type="datetime-local" value={globalForm.effectiveFrom} onChange={(e) => setGlobalForm((x) => ({ ...x, effectiveFrom: e.target.value }))} /></label>

          <div className="ofs-preview">
            <span>Preview sobre $30</span>
            {globalPreview.valid ? (
              <div>
                <p><span>Wompi</span><strong>{moneyFromCents(globalPreview.processorCost)}</strong></p>
                <p><span>Fee Loop</span><strong>{moneyFromCents(globalPreview.merchantFee)}</strong></p>
                <p><span>Negocio recibe</span><strong>{moneyFromCents(globalPreview.merchantPayable)}</strong></p>
                <p className="highlight"><span>Margen Loop</span><strong>{moneyFromCents(globalPreview.loopMargin)}</strong></p>
              </div>
            ) : <small>Completa las cuatro tarifas para ver el preview.</small>}
          </div>

          <button type="button" className="ofs-primary" onClick={saveGlobal} disabled={savingGlobal || loading || !globalPreview.valid}>
            {savingGlobal ? "Guardando…" : "Crear nueva tarifa global"}
          </button>
          <p className="ofs-footnote">Crear una nueva tarifa no modifica transacciones anteriores. La fecha efectiva decide desde cuándo aplica.</p>
        </article>) : (<article className="ofs-card">
          <div className="ofs-card-head">
            <div>
              <span className="ofs-card-kicker">Negocio</span>
              <h3>Tarifa del negocio</h3>
            </div>
            <span className="ofs-pill ofs-pill-soft">Opcional</span>
          </div>

          <label className="ofs-select-field">
            <span>Negocio</span>
            <select value={selectedBusinessId} onChange={onBusinessChange}>
              <option value="">Seleccionar negocio</option>
              {businesses.map((business) => (
                <option key={business.id} value={business.id}>{businessName(business)}</option>
              ))}
            </select>
          </label>

          <div className="ofs-current-override">
            <span>Tarifa efectiva del negocio</span>
            <strong>{activeOverride ? `${percentFromBps(activeOverride.merchant?.percentBps)} + ${moneyFromCents(activeOverride.merchant?.fixedCents)}` : selectedBusinessId ? "Usa tarifa global" : "Selecciona un negocio"}</strong>
          </div>

          <div className="ofs-fields ofs-fields-wide">
            <label><span>Porcentaje Loop</span><div><input inputMode="decimal" placeholder={globalForm.merchantPercent || "4.00"} value={overrideForm.merchantPercent} onChange={(e) => setOverrideForm((x) => ({ ...x, merchantPercent: e.target.value }))} /><b>%</b></div></label>
            <label><span>Fijo / transacción</span><div><b>$</b><input inputMode="decimal" placeholder={globalForm.merchantFixed || "0.50"} value={overrideForm.merchantFixed} onChange={(e) => setOverrideForm((x) => ({ ...x, merchantFixed: e.target.value }))} /></div></label>
          </div>

          <label className="ofs-date-field"><span>Aplicar desde</span><input type="datetime-local" value={overrideForm.effectiveFrom} onChange={(e) => setOverrideForm((x) => ({ ...x, effectiveFrom: e.target.value }))} /></label>
          <label className="ofs-note-field"><span>Nota interna</span><input type="text" maxLength={500} placeholder="Ej.: tarifa negociada por lanzamiento" value={overrideForm.note} onChange={(e) => setOverrideForm((x) => ({ ...x, note: e.target.value }))} /></label>

          <div className="ofs-preview">
            <span>Preview efectivo sobre $30</span>
            {overridePreview.valid ? (
              <div>
                <p><span>Wompi</span><strong>{moneyFromCents(overridePreview.processorCost)}</strong></p>
                <p><span>Fee Loop</span><strong>{moneyFromCents(overridePreview.merchantFee)}</strong></p>
                <p><span>Negocio recibe</span><strong>{moneyFromCents(overridePreview.merchantPayable)}</strong></p>
                <p className="highlight"><span>Margen Loop</span><strong>{moneyFromCents(overridePreview.loopMargin)}</strong></p>
              </div>
            ) : <small>Completa la tarifa para ver el preview.</small>}
          </div>

          <button type="button" className="ofs-primary" onClick={saveOverride} disabled={savingOverride || loading || !selectedBusinessId || !overrideForm.merchantPercent || !overrideForm.merchantFixed}>
            {savingOverride ? "Guardando…" : "Guardar tarifa del negocio"}
          </button>
          <p className="ofs-footnote">Esta tarifa cambia únicamente lo que Loop cobra a este negocio; nunca cambia el costo Wompi configurado globalmente.</p>
        </article>)}
        <button className="ofs-secondary" disabled={savingGlobal || savingOverride} onClick={() => setEditor(null)}>Cerrar</button></div>
      </AccessibleModal>}

      <article className="ofs-card ofs-history">
        <div className="ofs-card-head">
          <div>
            <span className="ofs-card-kicker">Historial</span>
            <h3>Tarifas globales creadas</h3>
          </div>
          <span className="ofs-pill ofs-pill-soft">{data?.schedules?.length || 0}</span>
        </div>
        {data?.schedules?.length ? (
          <div className="ofs-history-table">
            {data.schedules.slice(0, 12).map((schedule) => (
              <div key={schedule.id || schedule.scheduleId}>
                <span>{new Date(schedule.effectiveFrom).toLocaleString("es-PA")}</span>
                <strong>{percentFromBps(schedule.processor?.percentBps)} + {moneyFromCents(schedule.processor?.fixedCents)}</strong>
                <strong>{percentFromBps(schedule.merchant?.percentBps)} + {moneyFromCents(schedule.merchant?.fixedCents)}</strong>
                <em>{schedule.scheduleId}</em>
              </div>
            ))}
          </div>
        ) : <p className="ofs-empty">Todavía no hay tarifas guardadas.</p>}
      </article>
    </section>
  );
}

const styles = `
  .owner-finance-settings { display: grid; gap: 18px; }
  .ofs-head { display:flex; justify-content:space-between; gap:20px; align-items:flex-start; }
  .ofs-head h2 { margin:4px 0 6px; font-size:28px; letter-spacing:-.04em; }
  .ofs-head p { margin:0; color:#64748b; max-width:780px; line-height:1.55; }
  .ofs-kicker,.ofs-card-kicker { color:#00675f; text-transform:uppercase; letter-spacing:.14em; font-size:11px; font-weight:900; }
  .ofs-secondary,.ofs-primary { border:0; border-radius:12px; min-height:42px; padding:0 16px; font-weight:850; cursor:pointer; }
  .ofs-secondary { background:#eef6f5; color:#00675f; }
  .ofs-primary { background:#00675f; color:white; width:100%; margin-top:14px; }
  .ofs-primary:disabled,.ofs-secondary:disabled { opacity:.5; cursor:not-allowed; }
  .ofs-alert { border-radius:14px; padding:12px 14px; font-size:13px; font-weight:700; }
  .ofs-error { background:#fff1f2; color:#9f1239; border:1px solid #fecdd3; }
  .ofs-success { background:#ecfdf5; color:#065f46; border:1px solid #a7f3d0; }
  .ofs-warning { background:#fffbeb; color:#92400e; border:1px solid #fde68a; }
  .ofs-status-grid { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:12px; }
  .ofs-status-grid article,.ofs-card { border:1px solid #e2e8f0; background:white; border-radius:18px; box-shadow:0 8px 28px rgba(15,23,42,.035); }
  .ofs-status-grid article { padding:16px; display:grid; gap:6px; }
  .ofs-status-grid span,.ofs-current-override span { color:#64748b; font-size:12px; font-weight:750; }
  .ofs-status-grid strong { font-size:15px; }
  .ofs-status-grid small { color:#94a3b8; }
  .ofs-two-columns { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:16px; align-items:start; }
  .ofs-card { padding:18px; }
  .ofs-card-head { display:flex; justify-content:space-between; gap:12px; align-items:flex-start; margin-bottom:16px; }
  .ofs-card-head h3 { margin:4px 0 0; font-size:20px; letter-spacing:-.03em; }
  .ofs-pill { padding:6px 9px; border-radius:999px; font-size:11px; font-weight:850; background:#e7f5f3; color:#00675f; }
  .ofs-pill-soft { background:#f1f5f9; color:#64748b; }
  .ofs-rate-pair { display:grid; gap:16px; }
  .ofs-rate-pair h4 { margin:0 0 8px; font-size:13px; color:#334155; }
  .ofs-fields { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:10px; }
  .ofs-fields-wide { margin-top:14px; }
  .ofs-fields label,.ofs-date-field,.ofs-note-field,.ofs-select-field { display:grid; gap:6px; }
  .ofs-fields label>span,.ofs-date-field>span,.ofs-note-field>span,.ofs-select-field>span { font-size:11px; color:#64748b; font-weight:800; }
  .ofs-fields label>div { display:flex; align-items:center; gap:7px; border:1px solid #dbe3ea; border-radius:11px; padding:0 10px; background:#fff; min-height:42px; }
  .ofs-fields input,.ofs-date-field input,.ofs-note-field input,.ofs-select-field select { min-width:0; width:100%; border:1px solid #dbe3ea; border-radius:11px; min-height:42px; padding:0 11px; font:inherit; color:#0f172a; background:white; box-sizing:border-box; }
  .ofs-fields label>div input { border:0; padding:0; min-height:40px; outline:0; }
  .ofs-fields label>div b { color:#64748b; font-size:13px; }
  .ofs-date-field,.ofs-note-field,.ofs-select-field { margin-top:14px; }
  .ofs-current-override { margin-top:12px; padding:12px; border-radius:12px; background:#f8fafc; display:grid; gap:4px; }
  .ofs-current-override strong { font-size:14px; }
  .ofs-preview { margin-top:14px; background:#f7faf9; border:1px solid #dcebe8; border-radius:14px; padding:13px; }
  .ofs-preview>span { display:block; color:#64748b; font-size:11px; font-weight:850; text-transform:uppercase; letter-spacing:.08em; margin-bottom:8px; }
  .ofs-preview>div { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:7px; }
  .ofs-preview p { margin:0; display:flex; justify-content:space-between; gap:8px; background:white; padding:9px 10px; border-radius:9px; font-size:12px; }
  .ofs-preview p span { color:#64748b; }
  .ofs-preview p.highlight { background:#e9f6f3; color:#00675f; }
  .ofs-preview small { color:#94a3b8; }
  .ofs-footnote { margin:9px 2px 0; color:#94a3b8; font-size:11px; line-height:1.4; }
  .ofs-history { overflow:hidden; }
  .ofs-history-table { display:grid; }
  .ofs-history-table>div { display:grid; grid-template-columns:1.1fr .9fr .9fr 1.4fr; gap:12px; align-items:center; padding:10px 0; border-top:1px solid #edf2f7; font-size:12px; }
  .ofs-history-table>div span { color:#64748b; }
  .ofs-history-table>div em { color:#94a3b8; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-style:normal; }
  .ofs-empty { margin:0; color:#94a3b8; }
  @media (max-width: 900px) { .ofs-two-columns,.ofs-status-grid { grid-template-columns:1fr; } .ofs-history-table>div { grid-template-columns:1fr 1fr; } }
  @media (max-width: 560px) { .ofs-head { flex-direction:column; } .ofs-fields { grid-template-columns:1fr; } .ofs-preview>div { grid-template-columns:1fr; } .ofs-history-table>div { grid-template-columns:1fr; } }
`;
